import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../LoadingSpinner';
import {
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Copy,
  Eye,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Send
} from 'lucide-react';

interface BroadcastDetail {
  id: string;
  subject: string;
  content: string;
  status: string;
  total_contacts: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  started_sending_at: string | null;
  completed_at: string | null;
}

interface RecipientLog {
  id: string;
  recipient_email: string;
  recipient_name: string | null;
  status: string;
  error_message: string | null;
  sent_at: string | null;
}

interface BroadcastDetailPanelProps {
  broadcastId: string;
  onReuseAsTemplate: (subject: string, content: string) => void;
}

export function BroadcastDetailPanel({ broadcastId, onReuseAsTemplate }: BroadcastDetailPanelProps) {
  const [loading, setLoading] = useState(true);
  const [broadcast, setBroadcast] = useState<BroadcastDetail | null>(null);
  const [recipients, setRecipients] = useState<RecipientLog[]>([]);
  const [showContent, setShowContent] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState<'all' | 'sent' | 'failed'>('all');
  const [copiedId, setCopiedId] = useState(false);

  useEffect(() => {
    loadDetails();
  }, [broadcastId]);

  const loadDetails = async () => {
    try {
      setLoading(true);

      const [broadcastRes, recipientsRes] = await Promise.all([
        supabase
          .from('email_campaigns')
          .select('id, subject, content, status, total_contacts, sent_count, failed_count, created_at, started_sending_at, completed_at')
          .eq('id', broadcastId)
          .maybeSingle(),
        supabase
          .from('email_send_logs')
          .select('id, recipient_email, recipient_name, status, error_message, sent_at')
          .eq('campaign_id', broadcastId)
          .order('sent_at', { ascending: false }),
      ]);

      if (broadcastRes.error) throw broadcastRes.error;
      if (recipientsRes.error) throw recipientsRes.error;

      setBroadcast(broadcastRes.data);
      setRecipients(recipientsRes.data || []);
    } catch (err) {
      console.error('Error loading broadcast details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCopyId = () => {
    if (!broadcast) return;
    navigator.clipboard.writeText(broadcast.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  if (loading || !broadcast) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  const successRate = broadcast.total_contacts > 0
    ? Math.round((broadcast.sent_count / broadcast.total_contacts) * 100)
    : 0;

  const filteredRecipients = recipients.filter((r) => {
    if (recipientFilter === 'sent') return r.status === 'sent';
    if (recipientFilter === 'failed') return r.status === 'failed';
    return true;
  });

  const statusConfig: Record<string, { label: string; className: string }> = {
    draft: { label: 'Concept', className: 'bg-gray-100 text-gray-700' },
    sending: { label: 'Bezig met verzenden', className: 'bg-amber-100 text-amber-700' },
    completed: { label: 'Verzonden', className: 'bg-green-100 text-green-700' },
    failed: { label: 'Mislukt', className: 'bg-red-100 text-red-700' },
  };

  const currentStatus = statusConfig[broadcast.status] || statusConfig.draft;

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-lg font-semibold text-gray-900 leading-snug">{broadcast.subject}</h3>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${currentStatus.className}`}>
            {currentStatus.label}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <span className="font-mono truncate">{broadcast.id}</span>
          <button onClick={handleCopyId} className="p-0.5 hover:text-gray-600 transition-colors" title="Kopieer ID">
            {copiedId ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center gap-1.5 text-blue-600 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-2xl font-bold">{broadcast.total_contacts}</span>
          </div>
          <p className="text-xs text-blue-700">Totaal</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center gap-1.5 text-green-600 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span className="text-2xl font-bold">{broadcast.sent_count}</span>
          </div>
          <p className="text-xs text-green-700">Verzonden</p>
        </div>
        <div className={`rounded-lg p-4 text-center ${broadcast.failed_count > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
          <div className={`flex items-center justify-center gap-1.5 mb-1 ${broadcast.failed_count > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            <XCircle className="w-4 h-4" />
            <span className="text-2xl font-bold">{broadcast.failed_count}</span>
          </div>
          <p className={`text-xs ${broadcast.failed_count > 0 ? 'text-red-700' : 'text-gray-500'}`}>Mislukt</p>
        </div>
      </div>

      {broadcast.status === 'completed' && (
        <div>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-gray-600">Succespercentage</span>
            <span className="font-semibold text-gray-900">{successRate}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${successRate === 100 ? 'bg-green-500' : successRate >= 90 ? 'bg-green-400' : 'bg-amber-500'}`}
              style={{ width: `${successRate}%` }}
            />
          </div>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4 space-y-2.5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Aangemaakt</span>
          <span className="font-medium text-gray-900">{formatDate(broadcast.created_at)}</span>
        </div>
        {broadcast.started_sending_at && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Gestart</span>
            <span className="font-medium text-gray-900">{formatDate(broadcast.started_sending_at)}</span>
          </div>
        )}
        {broadcast.completed_at && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Voltooid</span>
            <span className="font-medium text-gray-900">{formatDate(broadcast.completed_at)}</span>
          </div>
        )}
      </div>

      <div>
        <button
          onClick={() => setShowContent(!showContent)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Eye className="w-4 h-4" />
            Inhoud bekijken
          </span>
          {showContent ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {showContent && (
          <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
              <p className="text-xs text-gray-500 font-medium">HTML Preview</p>
            </div>
            <div
              className="p-4 bg-white prose prose-sm max-w-none text-gray-800"
              dangerouslySetInnerHTML={{ __html: broadcast.content }}
            />
          </div>
        )}
      </div>

      <div>
        <button
          onClick={() => setShowRecipients(!showRecipients)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Mail className="w-4 h-4" />
            Ontvangers ({recipients.length})
          </span>
          {showRecipients ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {showRecipients && (
          <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
              {(['all', 'sent', 'failed'] as const).map((filter) => {
                const labels = { all: 'Alle', sent: 'Verzonden', failed: 'Mislukt' };
                const counts = {
                  all: recipients.length,
                  sent: recipients.filter((r) => r.status === 'sent').length,
                  failed: recipients.filter((r) => r.status === 'failed').length,
                };
                return (
                  <button
                    key={filter}
                    onClick={() => setRecipientFilter(filter)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      recipientFilter === filter
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {labels[filter]} ({counts[filter]})
                  </button>
                );
              })}
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
              {filteredRecipients.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-500 text-center">Geen ontvangers gevonden</p>
              ) : (
                filteredRecipients.map((recipient) => (
                  <div key={recipient.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 truncate">{recipient.recipient_email}</p>
                      {recipient.recipient_name && (
                        <p className="text-xs text-gray-500 truncate">{recipient.recipient_name}</p>
                      )}
                      {recipient.error_message && (
                        <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{recipient.error_message}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {recipient.status === 'sent' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={() => onReuseAsTemplate(broadcast.subject, broadcast.content)}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-sm font-medium text-blue-700 transition-colors"
      >
        <Copy className="w-4 h-4" />
        Hergebruik als template
      </button>
    </div>
  );
}
